/**
 * Composable pour les utilitaires partagés
 * 
 * Ce composable centralise les fonctions utilitaires communes utilisées dans plusieurs composants.
 * Il remplace les fonctions dupliquées comme formatDate qui étaient présentes dans :
 * - EmailHeader.vue, DjOrderPage.vue, useEmail.ts, DjCustomerListPage.vue, 
 * - DjOrderListPage.vue, DjOrderDetailPage.vue, DjCustomerPage.vue, etc.
 */
export const useUtils = () => {
  /**
   * Formate une date en format français
   */
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Non défini';
    try {
      return new Date(dateString).toLocaleString('fr-FR');
    } catch (error) {
      console.warn('Erreur lors du formatage de la date:', dateString);
      return 'Date invalide';
    }
  };

  /**
   * Normalise un texte pour la recherche (supprime les accents, met en minuscules)
   */
  const normalizeText = (text: string): string => {
    return text.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  /**
   * Formate un prix en euros
   */
  const formatPrice = (price: number | string | null | undefined): string => {
    if (price === null || price === undefined || price === '') return 'N/A';
    
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'N/A';
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(numPrice);
  };

  /**
   * Génère un prix aléatoire entre min et max (pour les tests)
   */
  const generateRandomPrice = (min: number = 0, max: number = 100): number => {
    return Math.round((Math.random() * (max - min) + min) * 100) / 100;
  };

  /**
   * Debounce une fonction
   */
  const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  /**
   * Tronque un texte à une longueur donnée
   */
  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };


  const getCivilityLabel = (civility: string) => {
    switch (civility) {
      case 'MR':
        return 'Monsieur'
      case 'MRS':
        return 'Madame'
      case 'MISS':
        return 'Mademoiselle'
      default:
        return civility
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Actif'
      case 'PENDING':
        return 'En attente'
      case 'INACTIVE':
        return 'Inactif'
      default:
        return status
    }
  }

  const formatPhone = (phone: string) => {
    // Format français : 33777341380 -> +33 7 77 34 13 80
    if (phone.startsWith('33')) {
      const formatted = phone.substring(2)
      return `+33 ${formatted.match(/.{1,2}/g)?.join(' ')}`
    }
    return phone
  }

  // Utilitaires pour les commandes
  const getOrderStatusClass = (status: string) => {
    switch (status) {
      case 'WAITING_SUPPLIER_APPROVAL':
        return 'bg-gray-200 text-gray-700'
      case 'VALIDATED':
        return 'bg-blue-100 text-blue-800'
      case 'SHIPPED':
        return 'bg-gray-200 text-gray-700'
      case 'DELIVERED':
        return 'bg-gray-600 text-white'
      case 'CANCELLED':
        return 'bg-gray-300 text-gray-600'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getOrderStatusLabel = (status: string) => {
    switch (status) {
      case 'WAITING_SUPPLIER_APPROVAL':
        return 'En attente'
      case 'VALIDATED':
        return 'Validée'
      case 'SHIPPED':
        return 'Expédiée'
      case 'DELIVERED':
        return 'Livrée'
      case 'CANCELLED':
        return 'Annulée'
      default:
        return status
    }
  }

  const getPaymentStatusClass = (status: string) => {
    switch (status) {
      case 'AUTHORIZED':
        return 'bg-gray-600 text-white'
      case 'PENDING':
        return 'bg-gray-200 text-gray-700'
      case 'FAILED':
        return 'bg-gray-300 text-gray-600'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }


  return {
    formatDate,
    normalizeText,
    formatPrice,
    generateRandomPrice,
    debounce,
    truncateText,
    getCivilityLabel,
    getStatusLabel,
    formatPhone,
    getOrderStatusClass,
    getOrderStatusLabel,
    getPaymentStatusClass
  };
};